/**
 * react-i18next configuration for 5mi.uz frontend-main.
 *
 * Supported locales : uz (default), ru
 * Namespace         : translation  (single)
 * Persistence       : localStorage key  →  "5mi_locale"
 * Fallback          : uz
 *
 * Usage in components:
 *   const { t } = useTranslation()
 *   t('home.greeting', { name: 'Ali' })
 *
 * Change language:
 *   import { setLocale } from '@/i18n/i18n'
 *   setLocale('ru')
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import uz from '@/locales/uz/translation.json'
import ru from '@/locales/ru/translation.json'

// ── Constants ─────────────────────────────────────────────────────────
export const SUPPORTED_LOCALES = ['uz', 'ru']
export const DEFAULT_LOCALE    = import.meta.env.VITE_DEFAULT_LOCALE ?? 'uz'
export const STORAGE_KEY       = '5mi_locale'

// ── Language detection (priority: localStorage → browser → default) ──
function detectLanguage() {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && SUPPORTED_LOCALES.includes(stored)) return stored

  const browser = (navigator.language ?? '').split('-')[0]
  if (SUPPORTED_LOCALES.includes(browser)) return browser

  return DEFAULT_LOCALE
}

// ── Init ─────────────────────────────────────────────────────────────
i18n
  .use(initReactI18next)
  .init({
    resources: {
      uz: { translation: uz },
      ru: { translation: ru },
    },

    lng:           detectLanguage(),
    fallbackLng:   'uz',
    supportedLngs: SUPPORTED_LOCALES,

    defaultNS: 'translation',
    ns:        ['translation'],

    interpolation: {
      escapeValue: false, // React already escapes
    },

    // Disable suspense by default — we handle loading states ourselves
    react: {
      useSuspense: false,
    },
  })

// ── Public helper ────────────────────────────────────────────────────
/**
 * Change the active locale and persist the choice.
 * @param {'uz'|'ru'} lng
 */
export function setLocale(lng) {
  if (!SUPPORTED_LOCALES.includes(lng)) return
  localStorage.setItem(STORAGE_KEY, lng)
  i18n.changeLanguage(lng)
}

export default i18n

